import { useLocation } from 'react-router-dom'

import { Colors } from 'cio-design-library/design-tokens'
import { useI18n } from 'builder/hooks/useI18n'
import { useUser } from 'builder/hooks/useUser'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useChromeExtension } from 'builder/hooks/useChromeExtension'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { usePostRedirectUrl } from 'builder/features/futureLearn/hooks/useGetRedirectUrl'
import { useResumeDistribution } from 'builder/views/ResumeDistributionView/hooks/useResumeDistribution'
import { selectors as initSelectors } from 'builder/modules/init'

import { Icon20, Icon24 } from 'builder/components/Icon'
import { TooltipV2 } from 'builder/components/Tooltip'
import { EXTENSION_URL } from 'builder/modules/jobTracking'
import { ChromeHoverPromote } from 'builder/components/JobTracking/ChromeHoverPromote/ChromeHoverPromote'

import { useCareerProfileData } from 'builder/hooks/useCareerProfileData'
import { useAutoApply } from 'builder/views/AutoApply/hooks/useAutoApply'
import { ISideMenuItems, ISubMenu, Position } from '../types'
import { Label, SubMenuIcon } from '../components/SideMenu/styles'
import { useOtherSubMenu } from './useOtherSubMenu'
import { useDocumentSubMenu } from './useDocumentSubMenu'
import { useJobSearchSubMenu } from './useJobSearchSubMenu'

const TRANSLATION = 'builder.side_menu'

export const useMenuItems = (isMobile?: boolean) => {
  const user = useUser()
  const { i18n } = useI18n()
  const location = useLocation()
  const feat = useFeaturesConfig()
  const { othersSubMenu } = useOtherSubMenu()
  const { documentSubMenu } = useDocumentSubMenu()
  const { jobSearchSubMenu } = useJobSearchSubMenu()
  const { isInstalled, canInstallExtension, getBrowserIcon } = useChromeExtension()
  const { isUserRdPromotedWithoutPlan, showRDUpsellPromoModal } = useResumeDistribution()
  const returnButtonData = useTypedSelector(initSelectors.returnButton)
  const { careerProfileData } = useCareerProfileData()
  const autoApply = useAutoApply()
  const { mutateAndRedirect } = usePostRedirectUrl()

  const isPostPremiumActive = user?.isPostPremium
  const showRdUpsellModal = isUserRdPromotedWithoutPlan && !isPostPremiumActive
  const PremiumIcon = isPostPremiumActive && (
    <Icon20.Crown width={24} height={24} color={Colors.Indigo70} />
  )

  const shouldShowRDTRWLPlansPage = () => {
    return careerProfileData?.additionalPlansPage === 'RD_TRWL' && user?.isFree
  }

  const isActiveSubMenu = (subMenu: ISubMenu[]) => {
    return subMenu.map(item => item.to).includes(`${location.pathname}${location.search}`)
  }

  const rdPlanLink = () => {
    return '/app/billing/plans?referrerPage=trwl'
  }

  const isActiveNestedMenu = (pathname: string) => {
    const PATH_NAME_LENGTH = 3
    // Ignore test for pathname no longer than 3 characters
    if (pathname.length < PATH_NAME_LENGTH) return false
    return location.pathname.includes(pathname)
  }

  const handleLearningHubClick = () => {
    mutateAndRedirect({})
  }

  const getRdLabel = () => {
    if (!feat.isUkOrUs() || user?.scopes.resumeDistributionCandidate) return
    const isPaidUser = !(user?.isFree || user?.isPostPremium)
    const isTRAndTrialUser = user?.isTrialUser && user?.scopes.kind === 'TR'
    const {
      recurring,
      count: rdCount,
      productId,
    } = user?.scopes.resumeDistributionAddonConfig || {}

    const isOneTime = recurring === false || productId === 6
    const count = isOneTime ? rdCount : isTRAndTrialUser ? 50 : 200

    const tooltip = isPaidUser
      ? i18n.t(`${TRANSLATION}.resume_distribution_tooltip_paid`, { count })
      : i18n.t(`${TRANSLATION}.resume_distribution_tooltip_free`)

    return (
      <TooltipV2 value={tooltip} width={320} multiline={true}>
        <Label>{count}</Label>
      </TooltipV2>
    )
  }

  const menuItems: ISideMenuItems[] = [
    {
      to: '/',
      enabled: !isMobile,
      text: i18n.t(`${TRANSLATION}.dashboard`),
      startDecoration: <Icon24.Dashboard />,
      startDecorationActive: <Icon24.DashboardActive />,
      eventParam: 'dashboard',
    },
    {
      to: '#',
      text: i18n.t(`${TRANSLATION}.documents`),
      enabled: true,
      subMenu: documentSubMenu.filter(item => item.enabled),
      active: isActiveSubMenu(documentSubMenu),
      endDecoration: <SubMenuIcon />,
      startDecoration: <Icon24.Documents />,
      startDecorationActive: <Icon24.DocumentsActive />,
      eventParam: 'documents',
    },
    {
      id: 'recommendations',
      to: '/job-search?view=recommendation',
      text: i18n.t(`${TRANSLATION}.recommended_jobs`),
      enabled: feat.features?.jobSearch,
      active: isActiveNestedMenu('/job-search'),
      startDecoration: <Icon24.Jobs />,
      startDecorationActive: <Icon24.JobsActive />,
      eventParam: 'job_search',
    },
    {
      id: 'job-tracking-menu',
      to: '/job-tracking',
      text: i18n.t(`${TRANSLATION}.job_tracker`),
      enabled: feat.features?.jobTracking,
      isPostPremium: isPostPremiumActive,
      endDecoration: isPostPremiumActive && PremiumIcon,
      startDecoration: <Icon24.NavJobTracker />,
      startDecorationActive: <Icon24.NavJobTrackerActive />,
      eventParam: 'job_tracking',
    },
    {
      id: 'auto-apply',
      to: '/auto-apply/start',
      text: i18n.t(`${TRANSLATION}.apply_4_me`),
      enabled: autoApply.isUserOnAutoApplyPlan,
      active: isActiveNestedMenu('/auto-apply'),
      startDecoration: <Icon24.AutoApply />,
      startDecorationActive: <Icon24.AutoApplyActive />,
      eventParam: 'auto_apply',
      onClick: !autoApply.isUserOnAutoApplyPlan ? autoApply.redirectToBillingPlans : undefined,
    },
    {
      to: '/interview-preparation/dashboard',
      text: i18n.t(`${TRANSLATION}.interview_prep`),
      enabled: feat.features?.interviewSchool,
      active: isActiveNestedMenu('/interview-preparation'),
      isPostPremium: isPostPremiumActive,
      endDecoration: PremiumIcon,
      startDecoration: <Icon24.InterviewPrep />,
      startDecorationActive: <Icon24.InterviewPrepActive />,
      eventParam: 'interview_prep',
    },
    {
      to: '/offer-analyzer',
      text: i18n.t(`${TRANSLATION}.salary_analyzer`),
      enabled: feat.features?.salaryAnalyzer,
      isPostPremium: isPostPremiumActive,
      endDecoration: PremiumIcon,
      startDecoration: <Icon24.SalaryAnalyzer />,
      startDecorationActive: <Icon24.SalaryAnalyzerActive />,
      eventParam: 'salary_analyzer',
    },
    {
      id: 'resume_distribution_menu',
      to: '/resume-distribution',
      hrefTarget: '_self',
      href: shouldShowRDTRWLPlansPage() ? rdPlanLink() : undefined,
      text: i18n.t(`${TRANSLATION}.resume_distribution`),
      enabled: feat?.showResumeDistribution(),
      active: isActiveNestedMenu('/resume-distribution'),
      isPostPremium: isPostPremiumActive,
      endDecoration: PremiumIcon || getRdLabel(),
      startDecoration: <Icon24.ResumeDistribution />,
      startDecorationActive: <Icon24.ResumeDistributionActive />,
      eventParam: 'resume_distribution',
      // onClick method override 'to' property and navigation should handle on click
      onClick: showRdUpsellModal ? showRDUpsellPromoModal : undefined,
    },
    {
      to: '#',
      text: i18n.t(`${TRANSLATION}.learning_hub`),
      enabled: feat?.features?.learningHub,
      startDecoration: <Icon24.StudySolidIcon />,
      startDecorationActive: <Icon24.StudySolidIcon />,
      endDecoration: <Label>{i18n.t(`${TRANSLATION}.new_label`)}</Label>,
      onClick: handleLearningHubClick,
      eventParam: 'learning_hub',
    },
    {
      to: '#',
      text: i18n.t(`${TRANSLATION}.job_search_method`),
      enabled: feat.features?.jobSearchPlan,
      subMenu: jobSearchSubMenu,
      active: isActiveSubMenu(jobSearchSubMenu),
      endDecoration: <SubMenuIcon />,
      startDecoration: <Icon24.JobSearchMethod />,
      startDecorationActive: <Icon24.JobSearchMethodActive />,
      eventParam: 'job_search_strategy',
    },
    {
      to: '/career-coaching',
      text: i18n.t(`${TRANSLATION}.coaching`),
      enabled: feat.features?.careerCoaching,
      startDecoration: <Icon24.Coaching />,
      startDecorationActive: <Icon24.CoachingActive />,
      eventParam: 'career_coaching',
    },
    {
      to: '#',
      text: i18n.t(`${TRANSLATION}.others`),
      enabled: !!othersSubMenu.filter(item => item.enabled)?.length,
      subMenu: othersSubMenu.filter(item => item.enabled),
      active: isActiveSubMenu(othersSubMenu),
      endDecoration: <SubMenuIcon />,
      startDecoration: <Icon24.NavMore />,
      startDecorationActive: <Icon24.NavMoreActive />,
      eventParam: 'other',
    },
    {
      to: '#',
      href: returnButtonData?.url || '#',
      text: returnButtonData?.text || i18n.t(`${TRANSLATION}.return_button_text`),
      startDecoration: <img src={returnButtonData?.icon} />,
      startDecorationActive: <img src={returnButtonData?.icon} />,
      position: 'end' as Position,
      enabled: returnButtonData?.enabled,
      eventParam: 'partner_redirect',
    },
    {
      to: '#',
      id: 'side-menu-chrome-extenstion',
      href: EXTENSION_URL,
      text: i18n.t(`${TRANSLATION}.chrome_extension`),
      enabled: !isInstalled && canInstallExtension && feat.features?.autoApply && !isMobile,
      position: returnButtonData?.enabled ? 'start' : ('end' as Position),
      startDecoration: getBrowserIcon(),
      startDecorationActive: getBrowserIcon(),
      eventParam: 'chrome_extension',
      popover: <ChromeHoverPromote />,
    },
  ].filter(item => item.enabled)

  return {
    isActiveSubMenu,
    menuItems: menuItems,
    isActiveNestedMenu,
  }
}
